import React, { useState } from "react";
import {
  DatePickerInput,
  DatePickerType,
  DatePickerValue,
} from "@mantine/dates";
import { Calendar, ChevronDown, X } from "lucide-react";
import "./NomadDatePicker.scss";

export interface NomadDatePickerProps<Type extends DatePickerType = "default"> {
  label?: string;
  placeholder?: string;
  value: any;
  onChange: (value: DatePickerValue<Type>) => void;
  minDate?: Date;
  maxDate?: Date;
  excludeDate?: (date: Date) => boolean;
  renderDate?: (date: Date) => React.ReactNode;
  disabled?: boolean;
}

const NomadDatePicker: React.FC<NomadDatePickerProps> = ({
  label,
  placeholder = "Select date",
  value,
  onChange,
  minDate,
  maxDate,
  excludeDate,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const isDateDisabled = (date: Date): boolean => {
    if (minDate) {
      const minDateStart = new Date(minDate);
      minDateStart.setHours(0, 0, 0, 0);
      if (date < minDateStart) return true;
    }
    if (maxDate) {
      const maxDateEnd = new Date(maxDate);
      maxDateEnd.setHours(23, 59, 59, 999);
      if (date > maxDateEnd) return true;
    }
    if (excludeDate && excludeDate(date)) return true;
    return false;
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date: Date): boolean => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null as any);
  };

  return (
    <div
      className={`nomad-datepicker-container ${
        disabled ? "nomad-datepicker-disabled" : ""
      }`}
    >
      {label && <label className="nomad-datepicker-label">{label}</label>}

      <DatePickerInput
        classNames={{
          root: "nomad-datepicker-root",
          input: "nomad-datepicker-input",
          wrapper: "nomad-datepicker-wrapper",
          section: "nomad-datepicker-section",
          day: "nomad-datepicker-day",
          weekday: "nomad-datepicker-weekday",
          month: "nomad-datepicker-month",
          calendarHeader: "nomad-datepicker-calendar-header",
          calendarHeaderControl: "nomad-datepicker-calendar-header-control",
          calendarHeaderLevel: "nomad-datepicker-calendar-header-level",
          monthsListControl: "nomad-datepicker-months-list-control",
          yearsListControl: "nomad-datepicker-years-list-control",
        }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        hideOutsideDates
        weekendDays={[]}
        clearable={false}
        valueFormat="MMM DD, YYYY"
        excludeDate={excludeDate}
        leftSection={
          <Calendar
            size={18}
            className={`nomad-datepicker-calendar-icon ${
              isOpen ? "active" : ""
            }`}
          />
        }
        rightSection={
          value ? (
            <button
              className="nomad-datepicker-clear-button"
              onClick={handleClear}
              type="button"
              disabled={disabled}
            >
              <X size={16} />
            </button>
          ) : (
            <ChevronDown
              size={18}
              className={`nomad-datepicker-chevron ${isOpen ? "rotated" : ""}`}
            />
          )
        }
        popoverProps={{
          shadow: "xl",
          radius: "md",
          withinPortal: true,
          zIndex: 1000,
          transitionProps: {
            transition: "pop",
            duration: 200,
          },
          classNames: {
            dropdown: "nomad-datepicker-dropdown",
          },
          onOpen: () => setIsOpen(true),
          onClose: () => setIsOpen(false),
        }}
        renderDay={(date) => {
          const dateDisabled = isDateDisabled(date);
          const today = isToday(date);
          const selected = isSelected(date);

          return (
            <div
              className={`
                nomad-datepicker-day-wrapper
                ${today ? "today" : ""}
                ${selected ? "selected" : ""}
                ${dateDisabled ? "unavailable" : ""}
              `}
            >
              <span className="nomad-datepicker-day-number">
                {date.getDate()}
              </span>
              {dateDisabled && !selected && (
                <span className="nomad-datepicker-unavailable-indicator" />
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

export default NomadDatePicker;
